import { supabase } from "../supabase/supabase";
import { whatsappApiClient, WhatsAppInstance } from "./whatsappApiClient";
import whatsappSocketService from "./whatsappSocket";
import * as Sentry from "@sentry/react";

export interface SyncResult {
  contactsProcessed: number;
  messagesFound: number;
  messagesAdded: number;
  leadsWithMessages: number;
  errors: string[];
}

export interface WhatsAppNumber {
  id: string;
  organization_id: string;
  phone_number: string;
  display_name?: string;
  qr_code_data?: string | null;
  session_data?: {
    instance_id?: string;
    phone_number?: string;
    connected_at?: string;
    persistent?: boolean;
    confirmed_at?: string;
    last_connected?: string; // Time of last successful connection
    whatsapp_phone?: string; // WhatsApp phone ID stored in session_data
    [key: string]: any;
  };
  status: 'pending_setup' | 'active' | 'inactive' | 'error' | 'disconnected';
  created_at: string;
  updated_at: string;
}

export interface WhatsAppMessage {
  id: string;
  whatsapp_number_id: string;
  lead_id: string;
  direction: 'inbound' | 'outbound';
  message_type: 'text' | 'image' | 'video' | 'audio' | 'document';
  content?: string;
  media_url?: string;
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  external_id?: string;
  error_message?: string;
  created_at: string;
  sent_at?: string;
  delivered_at?: string;
  read_at?: string;
}

class WhatsAppService {
  // WhatsApp number management
  async getWhatsAppNumbers(organizationId: string) {
    const { data, error } = await supabase
      .from("whatsapp_numbers")
      .select("*")
      .eq("organization_id", organizationId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data as WhatsAppNumber[];
  }

  async addWhatsAppNumber(whatsappNumber: {
    organization_id: string;
    phone_number: string;
    display_name?: string;
  }) {
    const { data, error } = await supabase
      .from("whatsapp_numbers")
      .insert(whatsappNumber)
      .select()
      .single();

    if (error) throw error;
    return data as WhatsAppNumber;
  }

  async updateWhatsAppNumber(id: string, updates: Partial<WhatsAppNumber>) {
    const { data, error } = await supabase
      .from("whatsapp_numbers")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data as WhatsAppNumber;
  }

  async deleteWhatsAppNumber(id: string) {
    const { error } = await supabase
      .from("whatsapp_numbers")
      .delete()
      .eq("id", id);

    if (error) throw error;
  }

  async getAvailableWhatsAppNumbers(organizationId: string) {
    // Get all WhatsApp numbers for the organization
    const { data: allNumbers, error: numbersError } = await supabase
      .from("whatsapp_numbers")
      .select("*")
      .eq("organization_id", organizationId)
      .eq("status", "active");

    if (numbersError) throw numbersError;

    // Get all WhatsApp numbers already assigned to campaigns
    const { data: assignedNumbers, error: assignedError } = await supabase
      .from("campaigns")
      .select("whatsapp_number_id")
      .not("whatsapp_number_id", "is", null);

    if (assignedError) throw assignedError;

    const assignedIds = assignedNumbers.map(c => c.whatsapp_number_id);
    
    // Filter out assigned numbers
    const availableNumbers = allNumbers?.filter(
      number => !assignedIds.includes(number.id)
    ) || [];

    return availableNumbers as WhatsAppNumber[];
  }

  // WhatsApp messaging
  async sendMessage(messageData: {
    whatsapp_number_id: string;
    lead_id: string;
    content: string;
    message_type?: 'text' | 'image' | 'video' | 'audio' | 'document';
    media_url?: string;
  }, retries = 2) {
    // Get WhatsApp number details to find the instance
    const { data: whatsappNumber, error: numberError } = await supabase
      .from("whatsapp_numbers")
      .select("session_data, phone_number")
      .eq("id", messageData.whatsapp_number_id)
      .single();

    if (numberError || !whatsappNumber?.session_data?.instance_id) {
      throw new Error('WhatsApp instance not found');
    }

    // Get lead phone number
    const { data: lead, error: leadError } = await supabase
      .from("leads")
      .select("phone_number")
      .eq("id", messageData.lead_id)
      .single();

    if (leadError || !lead) {
      throw new Error('Lead not found');
    }

    // Create message record
    const message = {
      ...messageData,
      direction: 'outbound',
      message_type: messageData.message_type || 'text',
      status: 'pending',
    };

    let data;
    
    try {
      const { data: msgData, error } = await supabase
        .from("whatsapp_messages")
        .insert(message)
        .select()
        .single();

      if (error) {
        Sentry.logger.error('Error creating message in database', { 
          error,
          log_source: 'whatsapp_service',
          lead_id: messageData.lead_id,
          whatsapp_number_id: messageData.whatsapp_number_id
        });
        // Create a temporary message object for tracking message status
        // We'll still try to send it even if DB insert fails
        data = {
          id: `temp-${Date.now()}`,
          ...message,
          created_at: new Date().toISOString(),
        };
        Sentry.logger.info('Created temporary message object for tracking', { 
          log_source: 'whatsapp_service',
          temporary_message_id: data.id,
          lead_id: messageData.lead_id
        });
      } else {
        data = msgData;
      }
    } catch (insertError) {
      Sentry.logger.error('Exception creating message in database', { 
        error: insertError,
        log_source: 'whatsapp_service',
        lead_id: messageData.lead_id,
        whatsapp_number_id: messageData.whatsapp_number_id
      });
      // Create temporary message object
      data = {
        id: `temp-${Date.now()}`,
        ...message,
        created_at: new Date().toISOString(),
      };
    }

    try {
      // Convert phone number to JID format (WhatsApp ID)
      const jid = lead.phone_number.replace(/[^0-9]/g, '') + '@s.whatsapp.net';
      
      // First, verify the connection status before sending
      let connectionStatus;
      
      try {
        connectionStatus = await this.checkConnectionStatus(messageData.whatsapp_number_id);
        
        if (connectionStatus.status !== 'connected') {
          Sentry.logger.warn('WhatsApp instance not connected', { 
            log_source: 'whatsapp_service',
            connection_status: connectionStatus.status,
            whatsapp_number_id: messageData.whatsapp_number_id
          });
          await this.updateMessageStatus(data.id, 'pending', { 
            error_message: `WhatsApp is not connected (status: ${connectionStatus.status})`
          });
          
          // Try to force reconnect first if instance exists
          if (connectionStatus.instanceId) {
            console.log('Trying to reconnect to existing instance before sending message');
            // Quick connection check without recreating instance
            await whatsappApiClient.getInstance(connectionStatus.instanceId);
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Check if reconnection helped
            const newStatus = await this.checkConnectionStatus(messageData.whatsapp_number_id);
            if (newStatus.status !== 'connected') {
              throw new Error(`WhatsApp is not connected (${newStatus.status})`);  
            }
          } else {
            throw new Error(`WhatsApp is not connected (${connectionStatus.status})`);  
          }
        }
      } catch (connectionError) {
        Sentry.logger.error('Connection check failed before sending message', { 
          error: connectionError,
          log_source: 'whatsapp_service',
          whatsapp_number_id: messageData.whatsapp_number_id
        });
        await this.updateMessageStatus(data.id, 'pending', { 
          error_message: 'Connection check failed: ' + (connectionError.message || 'Unknown error')
        });
      }
      
      // Send message using WhatsApp API
      const response = await whatsappApiClient.sendMessage(
        whatsappNumber.session_data.instance_id,
        jid,
        messageData.content
      );

      if (response && response.messageId) {
        // Update message with external ID and status
        await this.updateMessageStatus(data.id, 'sent', { 
          sent_at: new Date().toISOString(),
          external_id: response.messageId
        });
      } else {
        // Update status to failed
        await this.updateMessageStatus(data.id, 'failed', { 
          error_message: 'Failed to send message'
        });
      }
    } catch (error: any) {
      Sentry.logger.error('Error sending WhatsApp message', { 
        error,
        log_source: 'whatsapp_service',
        message_id: data.id,
        lead_id: messageData.lead_id,
        retries_remaining: retries
      });
      
      // Implement retry mechanism
      if (retries > 0) {
        Sentry.logger.info('Retrying message send', { 
          log_source: 'whatsapp_service',
          retries_remaining: retries,
          message_id: data.id
        });
        await this.updateMessageStatus(data.id, 'pending', { 
          error_message: `Retrying: ${error.message || 'Connection error'}`
        });
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        try {
          // Retry sending the message with one less retry attempt
          return await this.sendMessage(messageData, retries - 1);
        } catch (retryError: any) {
          Sentry.logger.error('Final retry failed', { 
            error: retryError,
            log_source: 'whatsapp_service',
            message_id: data.id,
            lead_id: messageData.lead_id
          });
          await this.updateMessageStatus(data.id, 'failed', { 
            error_message: `All retries failed: ${retryError.message || 'Unknown error'}`
          });
          throw retryError;
        }
      } else {
        // Update status to failed when all retries are exhausted
        await this.updateMessageStatus(data.id, 'failed', { 
          error_message: error.message || 'Failed to send message after all retries'
        });
        throw error;
      }
    }

    return data as WhatsAppMessage;
  }

  async getMessages(leadId: string, whatsappNumberId?: string, fetchFromApi: boolean = false) {
    // First get messages from our database
    let query = supabase
      .from("whatsapp_messages")
      .select(`
        *,
        whatsapp_numbers (
          phone_number,
          display_name,
          session_data
        )
      `)
      .eq("lead_id", leadId)
      .order("created_at", { ascending: true });

    if (whatsappNumberId) {
      query = query.eq("whatsapp_number_id", whatsappNumberId);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    
    // If fetchFromApi is true and whatsappNumberId is provided, also fetch from WhatsApp API
    if (fetchFromApi && whatsappNumberId) {
      try {
        // Get the WhatsApp number details directly
        const { data: whatsappNumber, error: numberError } = await supabase
          .from("whatsapp_numbers")
          .select("session_data")
          .eq("id", whatsappNumberId)
          .single();
          
        if (numberError || !whatsappNumber) {
          Sentry.logger.error('Failed to get WhatsApp number details', { 
          error: numberError,
          log_source: 'whatsapp_service',
          whatsapp_number_id: whatsappNumberId
        });
          return data as (WhatsAppMessage & { whatsapp_numbers: Pick<WhatsAppNumber, 'phone_number' | 'display_name'> })[];
        }
        
        // Get instance ID from the WhatsApp number record
        const instanceId = whatsappNumber.session_data?.instance_id;
        
        // Get lead's phone number
        const { data: lead, error: leadError } = await supabase
          .from("leads")
          .select("phone_number")
          .eq("id", leadId)
          .single();
          
        if (leadError || !lead || !instanceId) {
          Sentry.logger.error('Failed to get lead phone or instance ID', { 
          error: leadError,
          log_source: 'whatsapp_service',
          lead_id: leadId,
          whatsapp_number_id: whatsappNumberId
        });
          return data as (WhatsAppMessage & { whatsapp_numbers: Pick<WhatsAppNumber, 'phone_number' | 'display_name'> })[];
        }
        
        // Format number for the API
        const leadPhone = lead.phone_number;
        
        // Fetch messages from WhatsApp API
        const apiMessages = await this.getMessagesFromWhatsAppApi(instanceId, leadPhone, whatsappNumberId, leadId);
        
        // Return messages from API - these should already include our database messages
        return apiMessages;
      } catch (apiError) {
        Sentry.logger.error('Error fetching messages from WhatsApp API', { 
          error: apiError,
          log_source: 'whatsapp_service',
          lead_id: leadId,
          whatsapp_number_id: whatsappNumberId
        });
        // Fall back to database messages only
      }
    }
    
    return data as (WhatsAppMessage & { whatsapp_numbers: Pick<WhatsAppNumber, 'phone_number' | 'display_name'> })[];
  }
  
  /**
   * Syncs all WhatsApp messages for all leads associated with a WhatsApp number
   * @param whatsappNumberId The WhatsApp number ID to sync
   */
  async syncAllLeadMessages(whatsappNumberId: string): Promise<SyncResult> {
    Sentry.logger.info('Starting message sync for WhatsApp number', { 
    log_source: 'whatsapp_service',
    whatsapp_number_id: whatsappNumberId
  });
    const result: SyncResult = {
      contactsProcessed: 0,
      messagesFound: 0,
      messagesAdded: 0,
      leadsWithMessages: 0,
      errors: []
    };
    
    try {
      // Get WhatsApp number details
      const { data: whatsappNumber, error: numberError } = await supabase
        .from("whatsapp_numbers")
        .select("session_data, organization_id")
        .eq("id", whatsappNumberId)
        .single();
        
      if (numberError || !whatsappNumber?.session_data?.instance_id) {
        const error = `Failed to get WhatsApp number details: ${numberError?.message || 'No instance ID'}`;  
        Sentry.logger.error('Failed to get WhatsApp number details during sync', { 
          error,
          log_source: 'whatsapp_service',
          whatsapp_number_id: whatsappNumberId
        });
        result.errors.push(error);
        return result;
      }
      
      const instanceId = whatsappNumber.session_data.instance_id;
      const organizationId = whatsappNumber.organization_id;
      
      // Get all leads for this organization
      const { data: leads, error: leadsError } = await supabase
        .from("leads")
        .select("id, phone_number")
        .eq("organization_id", organizationId);
        
      if (leadsError) {
        const error = `Failed to get leads: ${leadsError.message}`;  
        Sentry.logger.error('Failed to get leads during sync', { 
          error: leadsError,
          log_source: 'whatsapp_service',
          organization_id: organizationId
        });
        result.errors.push(error);
        return result;
      }
      
      Sentry.logger.info('Found leads to check for messages', { 
        log_source: 'whatsapp_service',
        leads_count: leads?.length || 0,
        organization_id: organizationId
      });
      
      // Get all contacts from WhatsApp API
      const contacts = await whatsappApiClient.getContacts(instanceId);
      Sentry.logger.info('Retrieved contacts from WhatsApp API', { 
        log_source: 'whatsapp_service',
        contacts_count: contacts.length,
        instance_id: instanceId
      });
      
      // Create a map of phone numbers to leads for quick lookup
      const phoneToLeadMap: Record<string, any> = {};
      leads?.forEach(lead => {
        // Clean phone number for matching (remove non-numeric chars)
        const cleanedPhone = lead.phone_number.replace(/[^0-9]/g, '');
        phoneToLeadMap[cleanedPhone] = lead;
      });
      
      // Process each contact that matches a lead
      let processedContacts = 0;
      let leadsWithMessages = 0;
      
      // For each contact from WhatsApp
      for (const contact of contacts) {
        processedContacts++;
        
        // Extract and clean the phone number
        let contactPhone = contact.phone || contact.id;
        // Remove '@s.whatsapp.net' suffix if present (JID format)
        contactPhone = contactPhone.replace('@s.whatsapp.net', '');
        // Clean to just numbers
        const cleanedContactPhone = contactPhone.replace(/[^0-9]/g, '');
        
        // Look for a matching lead
        const matchingLead = phoneToLeadMap[cleanedContactPhone];
        
        if (matchingLead) {
          Sentry.logger.debug('Found matching lead for contact', { 
            log_source: 'whatsapp_service',
            contact_phone: cleanedContactPhone,
            lead_id: matchingLead.id
          });
          
          try {
            // Fetch and sync messages for this lead
            const messagesResult = await this.getMessagesFromWhatsAppApi(
              instanceId,
              matchingLead.phone_number,
              whatsappNumberId,
              matchingLead.id
            );
            
            if (messagesResult && messagesResult.length > 0) {
              leadsWithMessages++;
              result.messagesFound += messagesResult.length;
              Sentry.logger.info('Synced messages for lead', { 
              log_source: 'whatsapp_service',
              messages_count: messagesResult.length,
              lead_id: matchingLead.id
            });
            }
          } catch (syncError: any) {
            const error = `Error syncing messages for lead ${matchingLead.id}: ${syncError.message}`;  
            Sentry.logger.error('Error syncing messages for lead', { 
              error: syncError,
              log_source: 'whatsapp_service',
              lead_id: matchingLead.id
            });
            result.errors.push(error);
          }
        }
        
        // Add a delay to avoid rate limiting (100ms)
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      result.contactsProcessed = processedContacts;
      result.leadsWithMessages = leadsWithMessages;
      
      Sentry.logger.info('Completed message sync for WhatsApp number', { 
        log_source: 'whatsapp_service',
        whatsapp_number_id: whatsappNumberId,
        processed_contacts: processedContacts,
        leads_with_messages: leadsWithMessages
      });
      
      return result;
    } catch (error: any) {
      const errorMsg = `Error during message sync: ${error.message}`;
      Sentry.logger.error('Error during message sync', { 
        error,
        log_source: 'whatsapp_service',
        whatsapp_number_id: whatsappNumberId
      });
      result.errors.push(errorMsg);
      return result;
    }
  }
  
  /**
   * Fetches messages directly from WhatsApp API for a specific contact
   * and syncs them with our database
   */
  async getMessagesFromWhatsAppApi(instanceId: string, leadPhone: string, whatsappNumberId: string, leadId: string) {
    try {
      Sentry.logger.debug('Fetching messages from WhatsApp API', { 
        log_source: 'whatsapp_service',
        lead_id: leadId,
        lead_phone: leadPhone,
        instance_id: instanceId
      });
      
      // Fetch messages from WhatsApp API
      const apiResponse = await whatsappApiClient.getContactMessages(instanceId, leadPhone, 100);
      
      // Parse API response
      const messages = apiResponse.messages || [];
      Sentry.logger.info('Retrieved messages from WhatsApp API', { 
        log_source: 'whatsapp_service',
        messages_count: messages.length,
        lead_id: leadId
      });
      
      if (messages.length === 0) {
        // If no messages from API, return existing DB messages
        const { data } = await supabase
          .from("whatsapp_messages")
          .select(`*, whatsapp_numbers(phone_number, display_name)`)
          .eq("lead_id", leadId)
          .eq("whatsapp_number_id", whatsappNumberId)
          .order("created_at", { ascending: true });
          
        return data || [];
      }
      
      // Get existing message external IDs to avoid duplicates
      const { data: existingMessages } = await supabase
        .from("whatsapp_messages")
        .select("id, external_id, created_at")
        .eq("lead_id", leadId)
        .eq("whatsapp_number_id", whatsappNumberId);
      
      const existingExternalIds = new Set(existingMessages?.map(m => m.external_id).filter(Boolean) || []);
      
      // Convert API messages to our format
      const newMessages = [];
      for (const msg of messages) {
        // Skip messages we already have (by messageId)
        if (msg.messageId && existingExternalIds.has(msg.messageId)) {
          Sentry.logger.debug('Skipping duplicate message', { 
          log_source: 'whatsapp_service',
          message_id: msg.messageId,
          lead_id: leadId
        });
          continue;
        }
        
        const direction = msg.fromMe ? 'outbound' : 'inbound';
        
        // Extract content based on messageContent structure
        let content = '';
        if (msg.messageContent) {
          if (typeof msg.messageContent === 'string') {
            content = msg.messageContent;
          } else if (msg.messageContent.conversation) {
            content = msg.messageContent.conversation;
          } else if (msg.messageContent.extendedTextMessage?.text) {
            content = msg.messageContent.extendedTextMessage.text;
          } else if (msg.messageContent.imageMessage?.caption) {
            content = msg.messageContent.imageMessage.caption;
          } else {
            // Try to stringify the messageContent for inspection
            content = JSON.stringify(msg.messageContent);
          }
        } else {
          content = msg.body || msg.content || msg.text || msg.caption || '';
        }
        
        // Convert timestamp to ISO format - handle both string and number formats
        let timestamp = new Date().toISOString();
        if (msg.timestamp) {
          if (typeof msg.timestamp === 'number') {
            timestamp = new Date(msg.timestamp * 1000).toISOString();
          } else if (typeof msg.timestamp === 'string') {
            timestamp = new Date(msg.timestamp).toISOString();
          }
        }
        
        Sentry.logger.debug('Processing WhatsApp message', { 
          log_source: 'whatsapp_service',
          direction: direction,
          content_preview: content.substring(0, 30) + '...',
          timestamp: timestamp,
          lead_id: leadId
        });
        
        // Add to database if we don't already have it
        try {
          const { data: newMessage, error } = await supabase
            .from("whatsapp_messages")
            .insert({
              whatsapp_number_id: whatsappNumberId,
              lead_id: leadId,
              direction,
              message_type: 'text',  // Default to text for now
              content,
              status: direction === 'outbound' ? 'sent' : 'delivered',
              external_id: msg.messageId || msg.id, // Use messageId as primary identifier
              created_at: timestamp,
              sent_at: direction === 'outbound' ? timestamp : null,
              delivered_at: direction === 'inbound' ? timestamp : null
            })
            .select()
            .maybeSingle(); // Use maybeSingle to avoid errors
            
          if (error) {
            Sentry.logger.error('Error inserting new message from API', { 
              error,
              log_source: 'whatsapp_service',
              lead_id: leadId,
              message_direction: direction
            });
          } else if (newMessage) {
            Sentry.logger.debug('Successfully inserted new message', { 
              log_source: 'whatsapp_service',
              message_id: newMessage.id,
              lead_id: leadId
            });
            newMessages.push(newMessage);
          }
        } catch (insertError) {
          Sentry.logger.error('Failed to insert message from API', { 
            error: insertError,
            log_source: 'whatsapp_service',
            lead_id: leadId,
            message_external_id: msg.messageId || msg.id
          });
        }
      }
      
      Sentry.logger.info('Added new messages from WhatsApp API to database', { 
        log_source: 'whatsapp_service',
        new_messages_count: newMessages.length,
        lead_id: leadId
      });
      
      // Fetch all messages again to get the complete up-to-date list
      const { data: updatedMessages } = await supabase
        .from("whatsapp_messages")
        .select(`*, whatsapp_numbers(phone_number, display_name)`)
        .eq("lead_id", leadId)
        .eq("whatsapp_number_id", whatsappNumberId)
        .order("created_at", { ascending: true });
        
      return updatedMessages || [];
      
    } catch (error) {
      Sentry.logger.error('Error in getMessagesFromWhatsAppApi', { 
        error,
        log_source: 'whatsapp_service',
        instance_id: instanceId,
        lead_id: leadId
      });
      throw error;
    }
  }

  async updateMessageStatus(
    messageId: string, 
    status: WhatsAppMessage['status'],
    additionalFields?: {
      sent_at?: string;
      delivered_at?: string;
      read_at?: string;
      error_message?: string;
      external_id?: string;
    }
  ) {
    // Create updates object without updated_at since that column doesn't exist
    const updates = {
      status,
      ...additionalFields,
      // Don't include updated_at field as it's causing errors
      // The database might not have this column
    };

    try {
      // First, check if the message exists
      const { data: existingMessage, error: checkError } = await supabase
        .from("whatsapp_messages")
        .select("id")
        .eq("id", messageId)
        .maybeSingle(); // Use maybeSingle to avoid errors when no rows found
      
      if (checkError) {
        Sentry.logger.error('Error checking if message exists', { 
          error: checkError,
          log_source: 'whatsapp_service',
          message_id: messageId
        });
        return { id: messageId, status } as WhatsAppMessage;
      }
      
      // If message doesn't exist, return without trying to update
      if (!existingMessage) {
        Sentry.logger.warn('Message not found in database, skipping status update', { 
          log_source: 'whatsapp_service',
          message_id: messageId
        });
        return { id: messageId, status } as WhatsAppMessage;
      }
      
      // Message exists, proceed with update
      const { data, error } = await supabase
        .from("whatsapp_messages")
        .update(updates)
        .eq("id", messageId)
        .select()
        .maybeSingle(); // Use maybeSingle instead of single to avoid 406 errors

      if (error) {
        Sentry.logger.error('Error updating message status', { 
          error,
          log_source: 'whatsapp_service',
          message_id: messageId,
          status: status
        });
        throw error;
      }
      
      return (data || { id: messageId, status }) as WhatsAppMessage;
    } catch (error) {
      Sentry.logger.error('Failed to update message status in catch block', { 
        error,
        log_source: 'whatsapp_service',
        message_id: messageId
      });
      // Still return a basic object with the updated status so UI can reflect changes
      return { id: messageId, status } as WhatsAppMessage;
    }
  }

  // Instance and QR Code Management
  async createInstance(whatsappNumberId: string): Promise<WhatsAppInstance> {
    try {
      Sentry.logger.info('Creating WhatsApp instance for number', { 
        log_source: 'whatsapp_service',
        whatsapp_number_id: whatsappNumberId
      });
      // Don't pass any ID to the create endpoint - let the API generate a new one
      const response = await whatsappApiClient.createInstance();
      Sentry.logger.debug('API response for instance creation', { 
        log_source: 'whatsapp_service',
        instance_id: response?.data?.id
      });
      const instance = response.data;
      
      // Update the WhatsApp number with instance info
      await this.updateWhatsAppNumber(whatsappNumberId, {
        session_data: { instance_id: instance.id },
        status: 'pending_setup'
      });

      return instance;
    } catch (error) {
      Sentry.logger.error('Error creating WhatsApp instance', { 
        error,
        log_source: 'whatsapp_service',
        whatsapp_number_id: whatsappNumberId
      });
      throw error;
    }
  }

  async deleteAndRecreateInstance(whatsappNumberId: string): Promise<WhatsAppInstance> {
    try {
      // First, get the instance ID from the WhatsApp number
      const { data, error } = await supabase
        .from("whatsapp_numbers")
        .select("session_data")
        .eq("id", whatsappNumberId)
        .single();
        
      if (data?.session_data?.instance_id) {
        const instanceId = data.session_data.instance_id;
        Sentry.logger.info('Found existing instance, deleting it first', { 
          log_source: 'whatsapp_service',
          instance_id: instanceId,
          whatsapp_number_id: whatsappNumberId
        });
        
        try {
          // Try to delete the existing instance
          await whatsappApiClient.deleteInstance(instanceId);
          Sentry.logger.info('Successfully deleted instance', { 
            log_source: 'whatsapp_service',
            instance_id: instanceId
          });
        } catch (deleteError) {
          // If delete fails, log but continue with creating new instance
          Sentry.logger.error('Error deleting instance, continuing anyway', { 
            error: deleteError,
            log_source: 'whatsapp_service',
            instance_id: instanceId
          });
        }
      } else {
        Sentry.logger.info('No instance ID found, or invalid session data', { 
          log_source: 'whatsapp_service',
          whatsapp_number_id: whatsappNumberId
        });
      }
      
      // Update the database to clear the session data
      await this.updateWhatsAppNumber(whatsappNumberId, {
        session_data: null,
        status: 'pending_setup'
      });
      
      // Create a new instance 
      Sentry.logger.info('Creating new instance', { 
        log_source: 'whatsapp_service'
      });
      const response = await whatsappApiClient.createInstance();
      const newInstance = response.data;
      Sentry.logger.info('New instance created with QR code', { 
        log_source: 'whatsapp_service',
        instance_id: newInstance.id,
        has_qr_code: !!(newInstance.qrCode || newInstance.qr)
      });
      
      // Update the WhatsApp number with instance info
      await this.updateWhatsAppNumber(whatsappNumberId, {
        session_data: { instance_id: newInstance.id },
        status: 'pending_setup'
      });
      
      return newInstance;
    } catch (error) {
      Sentry.logger.error('Error in delete and recreate flow', { 
        error,
        log_source: 'whatsapp_service',
        whatsapp_number_id: whatsappNumberId
      });
      throw error;
    }
  }
  
  async getInstanceQRCode(instanceId: string, forceRecreate = false): Promise<string> {
    try {
      const response = await whatsappApiClient.getQRCode(instanceId);
      
      // Ensure we're returning a proper string
      if (!response.qrCode) {
        throw new Error('QR code not found in response');
      }
      
      // Check if it's an object and convert to JSON string 
      if (typeof response.qrCode === 'object' && response.qrCode !== null) {
        Sentry.logger.warn('QR code is an object, converting to string', { 
          log_source: 'whatsapp_service',
          qr_code_type: typeof response.qrCode
        });
        // Try to get a specific property that might contain the actual base64 string
        if ((response.qrCode as any).base64) {
          return String((response.qrCode as any).base64);
        } else if ((response.qrCode as any).data) {
          return String((response.qrCode as any).data);
        } else if ((response.qrCode as any).image) {
          return String((response.qrCode as any).image);
        } else {
          // If we can't find a likely property, just return empty string
          return '';
        }
      }
      
      // Log the QR code for debugging
      Sentry.logger.debug('QR code from API client', { 
        log_source: 'whatsapp_service',
        qr_code_type: typeof response.qrCode,
        has_qr_code: !!response.qrCode,
        preview: response.qrCode ? response.qrCode.substring(0, 50) + '...' : 'null'
      });
      
      // Ensure we're returning a string type - but check for data: prefix first
      if (typeof response.qrCode === 'string' && response.qrCode.startsWith('data:')) {
        Sentry.logger.debug('Returning data URL directly', { 
          log_source: 'whatsapp_service'
        });
        return response.qrCode;
      }
      
      const qrCode = String(response.qrCode);
      return qrCode;
    } catch (error: any) {
      // Special handling for reconnection case
      if (error.message === 'RECONNECTING') {
        Sentry.logger.info('Instance is reconnecting, QR code not available', { 
          log_source: 'whatsapp_service'
        });
        throw new Error('RECREATE_INSTANCE');
      }
      
      Sentry.logger.error('Error getting QR code', { 
        error,
        log_source: 'whatsapp_service'
      });
      throw error;
    }
  }

  async checkInstance(whatsappNumberId: string): Promise<WhatsAppInstance> {
    const { data, error } = await supabase
      .from("whatsapp_numbers")
      .select("session_data")
      .eq("id", whatsappNumberId)
      .single();

    if (error || !data?.session_data?.instance_id) {
      throw new Error('Instance not found');
    }

    const response = await whatsappApiClient.getInstance(data.session_data.instance_id);
    const instance = response.data;
    
    // Update the status in the database
    if (instance.isConnected) {
      await this.confirmSession(whatsappNumberId, {
        ...data.session_data,
        phone_number: instance.phoneNumber,
        connected_at: new Date().toISOString()
      });
    }

    return instance;
  }

  async confirmSession(whatsappNumberId: string, sessionData: any) {
    Sentry.logger.info('Confirming WhatsApp session', { 
    log_source: 'whatsapp_service',
    instance_id: sessionData?.instance_id,
    phone_number: sessionData?.phone_number
  });
    
    // Ensure instance_id is included in the session data
    if (!sessionData.instance_id) {
      Sentry.logger.error('No instance_id provided in session data', { 
        log_source: 'whatsapp_service'
      });
      return;
    }
    
    // Extract any additional data we want to store
    // Store everything inside session_data to avoid schema mismatches
    const sessionDataWithPhone = { ...sessionData };
    
    // Include phone number in session_data if available
    if (sessionData.phone_number) {
      sessionDataWithPhone.whatsapp_phone = sessionData.phone_number;
    }
    
    const updatedData = {
      session_data: {
        ...sessionDataWithPhone,
        persistent: true, // Mark this as a persistent connection to use later
        confirmed_at: new Date().toISOString(),
        last_connected: new Date().toISOString() // Store connection time inside session_data
      },
      status: 'active' as const,
      qr_code_data: null // Clear QR code once connected
    };
    
    try {
      const result = await this.updateWhatsAppNumber(whatsappNumberId, updatedData);
      Sentry.logger.info('WhatsApp session confirmed and stored in database', { 
        log_source: 'whatsapp_service',
        whatsapp_number_id: whatsappNumberId,
        instance_id: sessionData.instance_id
      });
      return result;
    } catch (error) {
      Sentry.logger.error('Error saving WhatsApp session to database', { 
        error,
        log_source: 'whatsapp_service',
        whatsapp_number_id: whatsappNumberId
      });
      throw error;
    }
  }

  async checkConnectionStatus(whatsappNumberId: string): Promise<{
    status: string;
    instanceId?: string;
    isPersistent?: boolean;
    needsReauthentication?: boolean;
  }> {
    try {
      // Get the WhatsApp number to find the instance ID
      const { data: whatsappNumber, error } = await supabase
        .from("whatsapp_numbers")
        .select("session_data, status")
        .eq("id", whatsappNumberId)
        .single();

      if (error || !whatsappNumber) {
        return { status: 'error' };
      }

      const instanceId = whatsappNumber.session_data?.instance_id;
      if (!instanceId) {
        return { status: 'pending_setup' };
      }

      // Check if this is a persistent connection that was previously confirmed
      const isPersistent = whatsappNumber.session_data?.persistent === true;
      
      // Check instance status using API
      try {
        const statusResponse = await whatsappApiClient.getStatus(instanceId);
        
        // Check if connected
        if (statusResponse.status === 'connected') {
          // Update status in database if needed
          if (whatsappNumber.status !== 'active') {
            // Get instance details to update phone number
            const instanceResponse = await whatsappApiClient.getInstance(instanceId);
            const instance = instanceResponse.data;
            
            await this.confirmSession(whatsappNumberId, {
              ...whatsappNumber.session_data,
              phone_number: instance.phoneNumber,
              connected_at: new Date().toISOString()
            });
          }
          
          return { 
            status: 'connected', 
            instanceId, 
            isPersistent 
          };
        } else if (statusResponse.status === 'connecting') {
          return { 
            status: 'pending_setup',
            instanceId,
            isPersistent
          };
        } else {
          return { 
            status: statusResponse.status,
            instanceId,
            isPersistent
          };
        }
      } catch (apiError: any) {
        Sentry.logger.error('Error getting instance status', { 
          error: apiError,
          log_source: 'whatsapp_service',
          instance_id: instanceId,
          whatsapp_number_id: whatsappNumberId
        });
        
        // Check for 404 error specifically - indicates instance no longer exists
        const is404Error = apiError.response?.status === 404 || 
                         (apiError.message && apiError.message.includes('404')) ||
                         apiError.status === 404;
        
        if (is404Error) {
          Sentry.logger.info('Instance not found (404) - needs re-authentication with QR code', { 
            log_source: 'whatsapp_service',
            instance_id: instanceId,
            whatsapp_number_id: whatsappNumberId
          });
          
          // Update number record to indicate instance is invalid
          await this.updateWhatsAppNumber(whatsappNumberId, {
            status: 'error',
            session_data: {
              ...whatsappNumber.session_data,
              error: 'Instance not found - requires re-authentication',
              error_time: new Date().toISOString(),
              requires_new_qr: true
            }
          });
          
          return { 
            status: 'disconnected', 
            instanceId, 
            isPersistent,
            needsReauthentication: true
          };
        }
        
        // For other errors, handle based on persistence
        if (isPersistent) {
          return { 
            status: 'disconnected', 
            instanceId, 
            isPersistent 
          };
        }
        return { status: 'pending_setup' };
      }
    } catch (error) {
      Sentry.logger.error('Error checking connection status', { 
        error,
        log_source: 'whatsapp_service',
        whatsapp_number_id: whatsappNumberId
      });
      return { status: 'error' };
    }
  }
  
  async pollConnectionStatus(whatsappNumberId: string, instanceId: string, 
    onStatusChange: (status: string) => void, 
    maxAttempts = 30, // Poll for 5 minutes (10s * 30)
    interval = 10000  // Poll every 10 seconds
  ): Promise<void> {
    let attempts = 0;
    
    const checkStatus = async () => {
      try {
        // Check status using API
        const statusResponse = await whatsappApiClient.getStatus(instanceId);
        const status = statusResponse.status;
        
        Sentry.logger.debug('Polling status', { 
          log_source: 'whatsapp_service',
          attempt: attempts,
          max_attempts: maxAttempts,
          status: status,
          instance_id: instanceId
        });
        onStatusChange(status);
        
        // If connected or max attempts reached, stop polling
        if (status === 'connected' || attempts >= maxAttempts) {
          if (status === 'connected') {
            // Use phone number from status response if available
            const phoneNumber = statusResponse.phoneNumber || '';
            const timestamp = statusResponse.timestamp || new Date().toISOString();
            
            Sentry.logger.info('Confirming WhatsApp session during polling', { 
              log_source: 'whatsapp_service',
              whatsapp_number_id: whatsappNumberId,
              phone_number: phoneNumber
            });
            
            // Store complete instance details including raw response for troubleshooting
            await this.confirmSession(whatsappNumberId, {
              instance_id: instanceId,
              phone_number: phoneNumber,
              connected_at: timestamp,
              raw_status: statusResponse.rawResponse,
              device_info: {
                id: statusResponse.id,
                last_check: new Date().toISOString() 
              }
            });
            
            Sentry.logger.info('WhatsApp instance successfully stored in database', { 
              log_source: 'whatsapp_service',
              instance_id: instanceId
            });
          }
          return;
        }
        
        // Continue polling
        attempts++;
        setTimeout(checkStatus, interval);
      } catch (error) {
        Sentry.logger.error('Error polling connection status', { 
          error,
          log_source: 'whatsapp_service',
          instance_id: instanceId
        });
        onStatusChange('error');
      }
    };
    
    // Start polling
    checkStatus();
  }
}

export const whatsappService = new WhatsAppService();